import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import DashboardPage from "@/components/DashboardPage";
import { Button } from "@/components/form/Button";
import { KitButton, kitButtonClass } from "../../components/kit/Button";
import {
  formatLabel,
  type BrandCalendarSettings,
  type CalendarPlanSlot,
  type CalendarPlatform,
} from "@/types/contentCalendar.type";
import type { BrandMemory, BrandMemorySource } from "../../settings/api/useBrandMemories";
import { WallBarSpacer, WallViewHeader } from "../../components/WallBar";
import { platformLabel } from "../../components/PlatformGlyph";
import { monthLabel, slotUtcDay } from "../calendar.utils";
import {
  publicationIntegrityOf,
  type PublicationIntegrityRead,
} from "../publicationIntegrity";

/**
 * A month, closed, and what the next one inherits from it.
 *
 * THE SECOND HALF IS THE POINT. A month that shipped is a record; a month that
 * shipped and left something behind makes the next one cheaper. The thing it
 * leaves behind already exists and is already load-bearing: BrandMemory is
 * "short, durable preferences about what this brand's client and team want",
 * written from client review feedback and by hand, and INJECTED IN FULL into
 * the planning, ideation and production-brief prompts. Three runners read it.
 *
 * It has never been shown at the moment it is earned. It lives in Settings,
 * filed under configuration, where nobody goes after signing off a month — so
 * the one screen where a person can see what a month taught the system, and
 * disagree with it, did not exist.
 *
 * SO "DO NOT CARRY" IS A DELETE, AND IT IS THE WHOLE MECHANISM. A preference
 * the studio disagrees with is otherwise silently steering every future run.
 * Removing it here is not tidying; it is the only human check on a loop that
 * would otherwise compound.
 *
 * WHAT IS NOT HERE. No "4 days from start to sign off", no "7 human
 * interventions". Those need a run model — a run row, steps, timings — and
 * there is none, so the numbers would be invented. The month reports what it
 * actually holds.
 */

const SOURCE_WORD: Record<BrandMemorySource, string> = {
  client_feedback: "From the client",
  chat: "From a conversation",
  manual: "Written by the team",
  import: "Imported by the team",
};

/* Figma 08, a past month: "Stop carrying this" rests as plain text; pressed, it
   opens a confirm card in place (radius 12) with Yes, drop it (danger) and Keep it. */
function Forget({ onConfirm }: { onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <button
        type="button"
        onClick={() => setArmed(true)}
        className="inline-flex h-8 items-center rounded-pill px-1 text-[13px] font-medium text-co-muted transition-colors duration-[var(--duration-fast)] hover:text-co-text md:justify-self-end"
      >
        Stop carrying this
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-card bg-co-surface-solid p-3.5 md:justify-self-end">
      <span className="text-[14px] font-semibold text-co-text">Stop carrying this?</span>
      <div className="flex flex-wrap items-center gap-2">
        <KitButton variant="danger" onClick={onConfirm}>
          Yes, drop it
        </KitButton>
        <KitButton variant="secondary" onClick={() => setArmed(false)}>
          Keep it
        </KitButton>
      </div>
    </div>
  );
}

function Figure({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="tt-figure tabular-nums text-co-text">{value}</span>
      <span className="tt-label uppercase text-co-faint">{label}</span>
    </div>
  );
}

type ArchiveState = "fully_live" | "partially_live" | "needs_action" | "not_live";

interface PublicationRecord {
  slot: CalendarPlanSlot;
  integrity: PublicationIntegrityRead;
  state: ArchiveState;
}

const ARCHIVE_STATE: Record<ArchiveState, { label: string; dot: string }> = {
  fully_live: { label: "Fully live", dot: "bg-co-live" },
  partially_live: { label: "Partially live", dot: "bg-co-attention" },
  needs_action: { label: "Needs action", dot: "bg-co-danger" },
  not_live: { label: "Not live", dot: "bg-co-faint" },
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function archiveStateOf(
  slot: CalendarPlanSlot,
  integrity: PublicationIntegrityRead,
): ArchiveState {
  if (integrity.complete) return "fully_live";
  if (integrity.publishedChannels.length > 0) return "partially_live";
  if (slot.status === "MISSED") return "not_live";
  if (slot.deliverable?.status === "FAILED" || integrity.needsAction) {
    return "needs_action";
  }
  return "not_live";
}

function archiveLabel(record: PublicationRecord): string {
  return record.slot.status === "MISSED" && record.state === "not_live"
    ? "Missed / not live"
    : ARCHIVE_STATE[record.state].label;
}

function channelList(channels: CalendarPlatform[]): string {
  const names = channels.map(platformLabel);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function channelSentence(prefix: string, channels: CalendarPlatform[]): string | null {
  const names = channelList(channels);
  return names ? `${prefix} ${names}.` : null;
}

function publicationDetail(
  slot: CalendarPlanSlot,
  integrity: PublicationIntegrityRead,
): string {
  const detail: string[] = [];
  if (slot.status === "MISSED") detail.push("The planned date was missed.");

  if (integrity.intendedChannels.length === 0) {
    detail.push("No publishing channels were set.");
    return detail.join(" ");
  }

  const live = channelSentence("Live on", integrity.publishedChannels);
  if (live) detail.push(live);

  if (slot.deliverable?.status !== "READY") {
    const targets = channelList(
      integrity.intendedChannels.filter(
        (channel) => !integrity.publishedChannels.includes(channel),
      ),
    );
    if (slot.deliverable?.status === "FAILED") {
      detail.push(`The asset failed before publishing${targets ? ` to ${targets}` : ""}.`);
    } else if (targets) {
      detail.push(`No publish-ready asset exists for ${targets}.`);
    }
    return detail.join(" ");
  }

  const failed = channelSentence("Publishing failed on", integrity.failedChannels);
  const manual = channelSentence("Manual posting is required for", integrity.manualChannels);
  const setup = channelSentence("Publishing setup is required for", integrity.setupChannels);
  const cancelled = channelSentence(
    "Publishing must be created again for",
    integrity.cancelledChannels,
  );
  const retrying = channelSentence("Automatic retry is scheduled for", integrity.retryingChannels);
  const publishing = channelSentence("Publishing now on", integrity.publishingChannels);
  const pending = channelSentence("Not yet queued on", integrity.pendingChannels);
  const queued = channelSentence("Queued but not live on", integrity.queuedChannels);
  [failed, manual, setup, cancelled, retrying, publishing, pending, queued].forEach((sentence) => {
    if (sentence) detail.push(sentence);
  });

  return detail.join(" ") || "Not live on the intended channels.";
}

function MixStat({ value, label }: { value: number; label: string }) {
  return (
    <span className="tt-read-s inline-flex items-baseline gap-2 text-co-muted">
      <strong className="font-medium tabular-nums text-co-text">{value}</strong>
      <span>{label}</span>
    </span>
  );
}

export default function DeliveredView({
  year,
  month,
  slots,
  settings,
  memories,
  signedBy,
  onForget,
  onPlanNext,
  onOpenPost,
  openPostLabel,
  secondary,
}: {
  year: number;
  /** 1-12. */
  month: number;
  slots: CalendarPlanSlot[];
  settings: BrandCalendarSettings | null;
  /**
   * What the brand carries into every future run. Owner-only: undefined for
   * everyone else, and the section is not drawn.
   */
  memories?: BrandMemory[];
  signedBy: string | null;
  /** Drops a preference so it stops steering later runs. */
  onForget?: (id: string) => void;
  onPlanNext?: () => void;
  /** Opens the publishing surface appropriate to this post's workflow. */
  onOpenPost?: (slot: CalendarPlanSlot) => void;
  /** Names that destination from the post's actual workflow stage. */
  openPostLabel?: (slot: CalendarPlanSlot) => string;
  /** Sideways moves off the record: the month's posts, the run of months. */
  secondary?: { label: string; onPress: () => void }[];
}) {
  const records = useMemo<PublicationRecord[]>(
    () =>
      [...slots]
        .sort(
          (a, b) =>
            new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime() ||
            a.order - b.order,
        )
        .map((slot) => {
          const integrity = publicationIntegrityOf(slot);
          return { slot, integrity, state: archiveStateOf(slot, integrity) };
        }),
    [slots],
  );
  const fullyLive = useMemo(
    () => records.filter(({ integrity }) => integrity.complete),
    [records],
  );
  const partiallyLive = useMemo(
    () => records.filter(({ state }) => state === "partially_live").length,
    [records],
  );
  const needsAction = useMemo(
    () => records.filter(({ state }) => state === "needs_action").length,
    [records],
  );
  const missedOrNotLive = useMemo(
    () => records.filter(({ state }) => state === "not_live").length,
    [records],
  );

  const mix = useMemo(() => {
    const liveSlots = fullyLive.map(({ slot }) => slot);
    const statics = liveSlots.filter((slot) => slot.mediaType === "STATIC").length;
    const horizontal = liveSlots.filter(
      (slot) => slot.mediaType !== "STATIC" && slot.orientation === "HORIZONTAL",
    ).length;
    return {
      statics,
      horizontal,
      vertical: liveSlots.length - statics - horizontal,
    };
  }, [fullyLive]);

  const sold = settings
    ? settings.staticPostsPerMonth +
      settings.verticalVideoPostsPerMonth +
      settings.horizontalVideoPostsPerMonth
    : null;

  return (
    <DashboardPage>
      <div className="flex w-full flex-col">
        <WallViewHeader
          title={`${monthLabel({ year, month })}, archived`}
          subhead={
            signedBy
              ? `${fullyLive.length} of ${slots.length} posts are fully live${sold !== null ? `; the month target was ${sold}` : ""}. Signed off by ${signedBy}.`
              : `${fullyLive.length} of ${slots.length} posts are fully live${sold !== null ? `; the month target was ${sold}` : ""}.`
          }
        >
          {secondary?.map((a) => (
            <KitButton key={a.label} variant="secondary" onClick={a.onPress}>
              {a.label}
            </KitButton>
          ))}
          {/* The page's one primary, and the only forward move on a month that
              is finished. It read as tertiary beside Review's neutral Open
              buttons. */}
          {onPlanNext ? (
            <Button size="small" onPress={onPlanNext}>
              Plan the next month
            </Button>
          ) : null}
        </WallViewHeader>

        <WallBarSpacer />

        <div className="flex flex-col gap-14 pt-8">
          <div className="border-b border-co-line pb-8">
            <div className="flex flex-wrap gap-x-16 gap-y-8">
              <Figure value={String(fullyLive.length)} label="Fully live" />
              <Figure value={String(partiallyLive)} label="Partially live" />
              <Figure value={String(needsAction)} label="Needs action" />
              <Figure value={String(missedOrNotLive)} label="Missed / not live" />
            </div>
            <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-t border-co-line pt-4">
              <span className="tt-label uppercase text-co-faint">Fully live media mix</span>
              <MixStat value={mix.statics} label="Static" />
              <MixStat value={mix.vertical} label="Video · 9:16 / 4:5" />
              <MixStat value={mix.horizontal} label="Video · 16:9" />
            </div>
            <p className="tt-read-s mt-3 max-w-[68ch] text-co-faint">
              Each post appears in one category. Partially live records name every remaining
              channel below, including failures and manual work.
            </p>
          </div>

          <section className="flex flex-col gap-4">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h2 className="tt-title-m font-extrabold uppercase text-co-text">
                Publication record
              </h2>
              <span className="tt-label tabular-nums uppercase text-co-faint">
                {records.length}
              </span>
            </div>
            <p className="tt-read-m max-w-[68ch] text-co-muted">
              The archive records every planned post and each intended channel. A month can be
              closed while publishing still needs attention.
            </p>

            {records.length === 0 ? (
              <p className="tt-read-m border-y border-co-line py-8 text-co-faint">
                This archived month has no planned posts.
              </p>
            ) : (
              <>
                <div className="flex flex-col rounded-card bg-co-surface px-4 lg:hidden">
                  {records.map((record) => {
                    const day = slotUtcDay(record.slot.scheduledAt);
                    const weekday = DAY_NAMES[new Date(record.slot.scheduledAt).getUTCDay()];
                    const state = ARCHIVE_STATE[record.state];
                    const actionLabel = openPostLabel?.(record.slot) ?? "Open post";
                    return (
                      <article
                        key={record.slot.id}
                        className="flex flex-col gap-4 border-b border-co-line px-1 py-5 last:border-b-0"
                      >
                        <div className="flex items-start gap-4">
                          <span className="flex shrink-0 items-end gap-1.5 tabular-nums">
                            <span className="font-section text-[30px] font-semibold leading-none text-co-text">
                              {String(day).padStart(2, "0")}
                            </span>
                            <span className="tt-label pb-0.5 text-[8px] uppercase text-co-faint">
                              {weekday}
                            </span>
                          </span>
                          <div className="min-w-0 flex-1">
                            <h3 className="tt-read-m font-medium leading-[1.4] text-co-text">
                              {record.slot.title}
                            </h3>
                            <p className="tt-read-s mt-1 text-co-muted">
                              {formatLabel(record.slot)} ·{" "}
                              {record.integrity.intendedChannels.length > 0
                                ? channelList(record.integrity.intendedChannels)
                                : "No channels set"}
                            </p>
                          </div>
                        </div>
                        <div>
                          <span className="tt-label inline-flex items-center gap-2 uppercase text-co-text">
                            <span
                              aria-hidden="true"
                              className={`size-1.5 shrink-0 rounded-full ${state.dot}`}
                            />
                            {archiveLabel(record)}
                          </span>
                          <p className="tt-read-s mt-1.5 leading-relaxed text-co-muted">
                            {publicationDetail(record.slot, record.integrity)}
                          </p>
                        </div>
                        {onOpenPost ? (
                          <button
                            type="button"
                            onClick={() => onOpenPost(record.slot)}
                            aria-label={`${actionLabel}: ${record.slot.title}`}
                            className="inline-flex min-h-11 w-full items-center justify-between border-t border-co-line pt-3 text-left text-[13px] font-medium text-co-text"
                          >
                            {actionLabel}
                            <ArrowRight className="size-3.5 shrink-0" strokeWidth={2} />
                          </button>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
                <div className="hidden min-w-0 overflow-x-auto rounded-card bg-co-surface lg:block">
                <table className="w-full min-w-[62rem] table-fixed border-collapse">
                  <caption className="sr-only">
                    Publication record for every planned post in {monthLabel({ year, month })}.
                  </caption>
                  <colgroup>
                    <col className="w-[8%]" />
                    <col className="w-[24%]" />
                    <col className="w-[16%]" />
                    <col className="w-[15%]" />
                    <col className="w-[27%]" />
                    {onOpenPost ? <col className="w-[10%]" /> : null}
                  </colgroup>
                  <thead>
                    <tr className="border-b border-co-line2 bg-co-recess">
                      {["Day", "Post", "Format", "Intended channels", "Publication"].map(
                        (heading) => (
                          <th
                            key={heading}
                            scope="col"
                            className="tt-read-s whitespace-nowrap px-3 py-2.5 text-left font-medium text-co-muted"
                          >
                            {heading}
                          </th>
                        ),
                      )}
                      {onOpenPost ? (
                        <th
                          scope="col"
                          className="tt-read-s whitespace-nowrap px-3 py-2.5 text-right font-medium text-co-muted"
                        >
                          Next
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => {
                      const day = slotUtcDay(record.slot.scheduledAt);
                      const weekday = DAY_NAMES[new Date(record.slot.scheduledAt).getUTCDay()];
                      const state = ARCHIVE_STATE[record.state];
                      const actionLabel = openPostLabel?.(record.slot) ?? "Open post";
                      return (
                        <tr
                          key={record.slot.id}
                          className="group/record border-b border-co-line align-top transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out-expo)] last:border-b-0 hover:bg-co-surface-solid"
                        >
                          <td className="border-r border-co-line px-3 py-4 tabular-nums">
                            <span className="flex items-end gap-1.5">
                              <span className="font-section text-[26px] font-semibold leading-none text-co-text">
                                {String(day).padStart(2, "0")}
                              </span>
                              <span className="tt-label pb-0.5 text-[8px] uppercase text-co-faint">
                                {weekday}
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <p className="tt-read-m font-medium leading-[1.4] text-co-text">
                              {record.slot.title}
                            </p>
                          </td>
                          <td className="tt-read-s px-3 py-4 text-co-muted">
                            {formatLabel(record.slot)}
                          </td>
                          <td className="tt-read-s px-3 py-4 text-co-muted">
                            {record.integrity.intendedChannels.length > 0
                              ? channelList(record.integrity.intendedChannels)
                              : "No channels set"}
                          </td>
                          <td className="px-3 py-4">
                            <span className="tt-label inline-flex items-center gap-2 uppercase text-co-text">
                              <span
                                aria-hidden="true"
                                className={`size-1.5 shrink-0 rounded-full ${state.dot}`}
                              />
                              {archiveLabel(record)}
                            </span>
                            <p className="tt-read-s mt-1.5 max-w-[44ch] leading-relaxed text-co-muted">
                              {publicationDetail(record.slot, record.integrity)}
                            </p>
                          </td>
                          {onOpenPost ? (
                            <td className="px-3 py-4 text-right">
                              <button
                                type="button"
                                onClick={() => onOpenPost(record.slot)}
                                aria-label={`${actionLabel}: ${record.slot.title}`}
                                className={kitButtonClass({ variant: "ghost" })}
                              >
                                {actionLabel}
                                <ArrowRight className="size-3 shrink-0" strokeWidth={2} />
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </section>

          {memories && (
          <section className="flex flex-col gap-4">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h2 className="tt-title-m font-extrabold uppercase text-co-text">
                What the next month carries
              </h2>
              <span className="tt-label tabular-nums uppercase text-co-faint">
                {memories.length}
              </span>
            </div>
            <p className="tt-read-m max-w-[62ch] text-co-muted">
              These go into every plan, every set of ideas and every brief this brand gets from
              now on. Drop one and it stops steering anything.
            </p>

            {memories.length === 0 ? (
              <p className="tt-read-m border-t border-co-line py-8 text-co-faint">
                Nothing learned yet. Preferences arrive here when a client explains a decision.
              </p>
            ) : (
              <ul className="flex flex-col border-t border-co-line">
                {memories.map((m) => (
                  <li
                    key={m.id}
                    className="grid grid-cols-1 gap-x-8 gap-y-2 border-b border-co-line py-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-baseline"
                  >
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <p className="tt-read-m leading-relaxed text-co-text">{m.body}</p>
                      <span className="tt-label uppercase text-co-faint">
                        {SOURCE_WORD[m.source]} ·{" "}
                        {new Date(m.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                        })}
                      </span>
                    </div>
                    {onForget && (
                      /* Two-step, in place. This deletes a preference that is
                         injected into every future plan, set of ideas and
                         brief — three runners read it — and it was the faintest
                         control on the page at 91x18, firing on one click. The
                         resting state stays quiet, because it should not be a
                         temptation; the commit is deliberate and wears
                         --co-attention, which is the sanctioned token for a
                         thing waiting on a person and the only colour this page
                         spends. */
                      <Forget onConfirm={() => onForget(m.id)} />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}
        </div>
      </div>
    </DashboardPage>
  );
}
